import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Icon } from 'shared/theme/link-ui-react/Icon';

const defaultHeight = '2.5em';

const activeSelector =
  '&:focus:not([disabled]):not([readonly]), &:hover:not([disabled]):not([readonly])';

const IconWrapper = styled.div`
  display: flex;
  padding: 0.625em;
  outline: none;
`;

const border = css`
  border-width: 0.0625em;
  border-style: solid;
  border-top-left-radius: 0.125em;
  border-bottom-left-radius: 0.125em;
  border-top-right-radius: 0.125em;
  border-bottom-right-radius: 0.125em;

  ${activeSelector} {
    /* both input and icon wrappers */
    &,
    & ~ ${IconWrapper} {
      border-width: 0.125em;
      border-color: ${p => p.theme.colors.aux.red};
    }
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: ${p => p.width};
  ${border}
`;

const SearchIcon = styled(Icon)`
  fill: ${p =>
    p.darkmode ? p.theme.colors.aux.white : p.theme.colors.aux.charcoal};
`;

const RawInput = styled.input`
  flex: 1 1 auto;
  order: 1;
  height: ${p => p.height || defaultHeight};
  width: ${p => p.width};
  box-sizing: border-box;
  min-width: 0.3125em;
  background: ${p =>
    p.readOnly ? p.theme.colors.aux.offWhite : p.theme.colors.aux.white};
  color: ${p => p.theme.colors.text.primary};
  font-family: ${p => p.theme.fontFamily};
  font-size: 1em;
  outline: none;
  border: none;
  background-color: transparent;

  &:disabled {
    cursor: not-allowed;
    background: ${p => p.theme.colors.aux.offWhite};
    & ~ ${IconWrapper} {
      cursor: not-allowed;
      background: ${p => p.theme.colors.aux.offWhite};
      fill: ${p => p.theme.colors.aux.darkGrey};
    }
  }
`;

const Error = styled.label`
  font-family: ${p => p.theme.fontFamily};
  font-size: 0.85em;
  letter-spacing: 0.01875em;
  padding-left: 0.125em;
  color: ${p => p.theme.colors.aux.red};
`;

class TableFilter extends PureComponent {
  focus = () => {
    this.input.value = '';
    this.props.onChange('');
    this.input.focus();
  };

  render() {
    const {
      label,
      iconSize,
      border,
      iconType,
      width,
      readOnly,
      error,
      onChange,
      darkmode,
      inline,
      ...props
    } = this.props;

    return (
      <InputWrapper width={width}>
        <RawInput
          {...props}
          ref={elem => (this.input = elem)}
          error={error}
          readOnly={readOnly}
          onChange={e => {
            onChange(e.target.value);
          }}
          placeholder={'Filter...'}
          onFocus={this.focus}
        />
        <IconWrapper
          tabIndex={0}
          border={border}
          onClick={this.focus}
          data-test-id="input-wrapper"
        >
          <SearchIcon
            icon={iconType}
            height={iconSize}
            width={iconSize}
            darkmode={darkmode ? 1 : 0}
          />
        </IconWrapper>
      </InputWrapper>
    );
  }
}

TableFilter.defaultProps = {
  label: 'Filter...',
  iconSize: '20',
  iconType: 'Search',
  width: '20%',
  darkmode: false,
  inline: true,
  onChange: () => {},
};

TableFilter.propTypes = {
  /**
   * label for the Search Input
   */
  label: PropTypes.string,
  /**
   * Size of the input in pixels - always a square
   */
  iconSize: PropTypes.string,
  /**
   * select the style of icon
   */
  iconType: PropTypes.string,
  /**
   * width of Search Input
   */
  width: PropTypes.string,
  darkmode: PropTypes.bool,
  /**
   * Set the input to read-only
   */
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
};

export default TableFilter;
